import { useCallback, useMemo } from 'react';
import { getTestResultsTableColumns } from '@elementary/common-ui/pages';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import {
  GroupedData,
  ColumnFilterOption,
} from '@elementary/common-ui/components/Table/types';
import { distinctArrayBy } from '@elementary/common-ui/utils/common';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import { TestExecutionField } from '@elementary/common-ui/services/gql/types';
import {
  TEST_RESULTS,
  TEST_RESULTS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import {
  DEFAULT_DAYS_BACK,
  PAGE_SIZE,
  GROUP_BY_MAX_CARDINALITY,
} from '@elementary/common-ui/constants';
import { TEST_RESULTS_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import {
  parseApiTestResultTableData,
  type ApiTestResultsTableData,
  type TestResultsTableData,
} from '@elementary/common-ui/components';
import { TestResultsChips } from '@elementary/common-ui/components/ChipList';
import useGroupedPagedQuery from 'shared/hooks/useGroupedPagedQuery';
import {
  ApiTestResultsFilterOptionsResponse,
  ApiTestResultsTableDataPagedResponse,
  getTestResultsFilterOptionsQuery,
  getTestResultsTableDataQuery,
} from './api';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from './consts';
import useTestResultsQueryContext from './useTestResultsQueryContext';
import useGroupStatusSummaries from './useGroupStatusSummaries';

const STATUS_COLUMN_ID = 'latestRunStatus';

const mapTestResultRows = (
  items: ApiTestResultsTableData[],
): TestResultsTableData[] =>
  distinctArrayBy(items, 'execution.subTestUniqueId').map(
    parseApiTestResultTableData,
  );

type Args = {
  search: string;
};

const useGroupedTestResults = ({ search }: Args) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const groupByColumnId = searchParams.get(TEST_RESULTS_GROUP_BY) || null;
  const onGroupByChange = useCallback(
    (next: string | null) => {
      setSearchParams((prev) => {
        if (next) prev.set(TEST_RESULTS_GROUP_BY, next);
        else prev.delete(TEST_RESULTS_GROUP_BY);
        return prev;
      });
    },
    [setSearchParams],
  );
  const isGrouped = !!groupByColumnId;

  const {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    testExecutionFilters: baseTestExecutionFilters,
    urlSort,
    sort,
    filterOptions,
    unscopedFilterOptions,
    isFilterOptionsFetching,
  } = useTestResultsQueryContext();

  const groupEnabled = isGrouped && hasSelectedNodes;

  const groupableColumnIds = useMemo(
    () =>
      Object.keys(unscopedFilterOptions).filter((id) => {
        const count = unscopedFilterOptions[id]?.length ?? 0;
        return count > 0 && count <= GROUP_BY_MAX_CARDINALITY;
      }),
    [unscopedFilterOptions],
  );

  const { data: groupOptionsData, isFetching: isGroupOptionsFetching } =
    useGraphqlQuery<ApiTestResultsFilterOptionsResponse>({
      queryKey: [
        TEST_RESULTS_FILTER_OPTIONS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        'grouped',
        groupByColumnId,
        baseTestExecutionFilters,
        search,
      ],
      query: getTestResultsFilterOptionsQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        search: search || null,
        testExecutionFilters: baseTestExecutionFilters,
      },
      enabled: groupEnabled,
      envId: envid,
    });

  const groupValues = useMemo<ColumnFilterOption[]>(() => {
    if (!groupByColumnId) return [];
    const parsed = parseGraphqlFilterOptions<TestExecutionField>(
      groupOptionsData?.latestTestExecutionsFilterOptions || [],
      FIELD_TO_COLUMN_MAP,
    );
    return parsed[groupByColumnId] ?? [];
  }, [groupOptionsData, groupByColumnId]);

  const showStatusSummary = isGrouped && groupByColumnId !== STATUS_COLUMN_ID;

  const statusValues = useMemo(
    () => (filterOptions[STATUS_COLUMN_ID] ?? []).map((option) => option.value),
    [filterOptions],
  );

  const statusTotalsByGroup = useGroupStatusSummaries({
    groupByColumnId: showStatusSummary ? groupByColumnId : null,
    statusValues,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    baseTestExecutionFilters,
    search,
    envid,
    enabled: groupEnabled && showStatusSummary,
  });

  const field = groupByColumnId
    ? COLUMN_TO_FIELD_MAP[groupByColumnId]
    : undefined;

  const formatGroupTitle = useMemo(() => {
    if (!groupByColumnId) return (value: string) => value;
    const columns = getTestResultsTableColumns([], filterOptions);
    const groupColumn = columns.find((c) => c.id === groupByColumnId);
    return (value: string) => groupColumn?.filterRenderer?.(value) ?? value;
  }, [filterOptions, groupByColumnId]);

  const titleToValue = useMemo(() => {
    const map = new Map<string, string>();
    groupValues.forEach((option) => {
      map.set(formatGroupTitle(option.value), option.value);
    });
    return map;
  }, [groupValues, formatGroupTitle]);

  const groupKeys = useMemo(
    () => (field ? groupValues.map((option) => option.value) : []),
    [field, groupValues],
  );

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<
      ApiTestResultsTableData,
      ApiTestResultsTableDataPagedResponse
    > => ({
      queryKey: [
        TEST_RESULTS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        urlFiltersDebounced,
        urlSort,
        search,
        field,
        value,
      ],
      query: getTestResultsTableDataQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        testExecutionFilters: field
          ? [
              ...baseTestExecutionFilters,
              { property: field, operator: 'equals', value: [value] },
            ]
          : baseTestExecutionFilters,
        sort,
        search: !search ? null : search,
        limit: PAGE_SIZE,
      },
      select: (d) => d.latestTestExecutionsPaged,
      enabled: groupEnabled && !!field,
      envId: envid,
    }),
    [
      envid,
      selectedIdsHash,
      selectedTestIds,
      selectedAssetIds,
      baseTestExecutionFilters,
      urlFiltersDebounced,
      urlSort,
      sort,
      search,
      groupEnabled,
      field,
    ],
  );

  const {
    groupFetchers,
    getRows,
    fetchNextGroupPage,
    isAnyFetching,
    isInitialLoading,
  } = useGroupedPagedQuery<
    ApiTestResultsTableData,
    ApiTestResultsTableDataPagedResponse,
    TestResultsTableData
  >({
    groupKeys,
    buildQueryOptions,
    mapItems: mapTestResultRows,
  });

  const groupedData = useMemo<GroupedData<TestResultsTableData>[]>(
    () =>
      groupValues.map((option) => ({
        title: formatGroupTitle(option.value),
        count: option.count,
        data: getRows(option.value),
        metadataRenderer: showStatusSummary
          ? () => (
              <TestResultsChips
                totals={statusTotalsByGroup[option.value]}
                size="medium"
                hideEmptyState
              />
            )
          : undefined,
      })),
    [
      groupValues,
      getRows,
      formatGroupTitle,
      showStatusSummary,
      statusTotalsByGroup,
    ],
  );

  const onGroupScrollToBottom = useCallback(
    (groupTitle: string) => fetchNextGroupPage(titleToValue.get(groupTitle)),
    [fetchNextGroupPage, titleToValue],
  );

  const isFetching =
    isFilterOptionsFetching ||
    isGroupOptionsFetching ||
    isAnyFetching ||
    isInitialLoading;

  return {
    groupBy: groupByColumnId,
    onGroupByChange,
    isGrouped,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    onGroupScrollToBottom,
    filterOptions,
    isFetching,
    isFilterOptionsFetching,
  };
};

export default useGroupedTestResults;
